import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { ApplicationRef, APP_INITIALIZER, DoBootstrap, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { Store, StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpLoggingInterceptor, Logger, LoggingService } from '@services/logging/logging.module';
import { AuthService } from '@shared/services/auth/auth.service';
import { HttpAuthInterceptor } from '@shared/services/auth/http-auth.interceptor';
import { IState, metaReducers, reducers, StateModule } from '@state/state.module';
import { ngxLoadingAnimationTypes, NgxLoadingModule } from 'ngx-loading';
import { AppInitService } from './app-init.service';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { LayoutModule } from './layout/layout.module';
import { RoutesModule } from './routes/routes.module';
import { SharedModule } from './shared/shared.module';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { IdentityServerConfigModule } from './auth/identity-server-config.module';
import { Token } from '@state/reducers/auth/auth.models';
import { registerLocaleData } from '@angular/common';
import { registerLocale } from 'i18n-iso-countries';

// Import Moment locale files
import 'moment/locale/en-gb';
import 'moment/locale/nl';
import 'moment/locale/fr';
import 'moment/locale/de';
import 'moment/locale/sv';
import 'moment/locale/es';
import 'moment/locale/it';
import 'moment/locale/nb';
import 'moment/locale/da';
import 'moment/locale/lt';
import 'moment/locale/fi';
import 'moment/locale/lv';
import 'moment/locale/cs';
import 'moment/locale/sl';
import 'moment/locale/ru';
import 'moment/locale/uk';
import 'moment/locale/tr';
import 'moment/locale/el';
import 'moment/locale/pt';
import 'moment/locale/pl';
import 'moment/locale/sk';

// Import locale for calendar
import localeEnGb from '@angular/common/locales/en-GB';
import localeNl from '@angular/common/locales/nl';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeSv from '@angular/common/locales/sv';
import localeEs from '@angular/common/locales/es';
import localeIt from '@angular/common/locales/it';
import localeNb from '@angular/common/locales/nb';
import localeDa from '@angular/common/locales/da';
import localeLt from '@angular/common/locales/lt';
import localeFi from '@angular/common/locales/fi';
import localeLv from '@angular/common/locales/lv';
import localeCs from '@angular/common/locales/cs';
import localeSl from '@angular/common/locales/sl';
import localeRu from '@angular/common/locales/ru';
import localeUk from '@angular/common/locales/uk';
import localeTr from '@angular/common/locales/tr';
import localeEl from '@angular/common/locales/el';
import localePt from '@angular/common/locales/pt';
import localePl from '@angular/common/locales/pl';
import localeSk from '@angular/common/locales/sk';

registerLocaleData(localeEnGb);
registerLocaleData(localeNl);
registerLocaleData(localeFr);
registerLocaleData(localeDe);
registerLocaleData(localeSv);
registerLocaleData(localeEs);
registerLocaleData(localeIt);
registerLocaleData(localeNb);
registerLocaleData(localeDa);
registerLocaleData(localeLt);
registerLocaleData(localeFi);
registerLocaleData(localeLv);
registerLocaleData(localeCs);
registerLocaleData(localeSl);
registerLocaleData(localeRu);
registerLocaleData(localeUk);
registerLocaleData(localeTr);
registerLocaleData(localeEl);
registerLocaleData(localePt);
registerLocaleData(localePl);
registerLocaleData(localeSk);

// Import locale for countries
import countryDataEn from 'i18n-iso-countries/langs/en.json';
import countryDataNl from 'i18n-iso-countries/langs/nl.json';
import countryDataFr from 'i18n-iso-countries/langs/fr.json';
import countryDataDe from 'i18n-iso-countries/langs/de.json';
import countryDataSv from 'i18n-iso-countries/langs/sv.json';
import countryDataEs from 'i18n-iso-countries/langs/es.json';
import countryDataIt from 'i18n-iso-countries/langs/it.json';
import countryDataNb from 'i18n-iso-countries/langs/nb.json';
import countryDataDa from 'i18n-iso-countries/langs/da.json';
import countryDataLt from 'i18n-iso-countries/langs/lt.json';
import countryDataFi from 'i18n-iso-countries/langs/fi.json';
import countryDataLv from 'i18n-iso-countries/langs/lv.json';
import countryDataCs from 'i18n-iso-countries/langs/cs.json';
import countryDataSl from 'i18n-iso-countries/langs/sl.json';
import countryDataRu from 'i18n-iso-countries/langs/ru.json';
import countryDataUk from 'i18n-iso-countries/langs/uk.json';
import countryDataTr from 'i18n-iso-countries/langs/tr.json';
import countryDataEl from 'i18n-iso-countries/langs/el.json';
import countryDataPt from 'i18n-iso-countries/langs/pt.json';
import countryDataPl from 'i18n-iso-countries/langs/pl.json';
import countryDataSk from 'i18n-iso-countries/langs/sk.json';

registerLocale(countryDataEn);
registerLocale(countryDataNl);
registerLocale(countryDataFr);
registerLocale(countryDataDe);
registerLocale(countryDataSv);
registerLocale(countryDataEs);
registerLocale(countryDataIt);
registerLocale(countryDataNb);
registerLocale(countryDataDa);
registerLocale(countryDataLt);
registerLocale(countryDataFi);
registerLocale(countryDataLv);
registerLocale(countryDataCs);
registerLocale(countryDataSl);
registerLocale(countryDataRu);
registerLocale(countryDataUk);
registerLocale(countryDataTr);
registerLocale(countryDataEl);
registerLocale(countryDataPt);
registerLocale(countryDataPl);
registerLocale(countryDataSk);

// Import all Froala Editor plugins
import 'froala-editor/js/plugins.pkgd.min';

// Import Froala Editor language files
import 'froala-editor/js/languages/fr';
import 'froala-editor/js/languages/nl';
import 'froala-editor/js/languages/de';
import 'froala-editor/js/languages/sv';
import 'froala-editor/js/languages/es';
import 'froala-editor/js/languages/it';
import 'froala-editor/js/languages/nb';
import 'froala-editor/js/languages/da';
import 'froala-editor/js/languages/fi';
import 'froala-editor/js/languages/cs';
import 'froala-editor/js/languages/sl';
import 'froala-editor/js/languages/ru';
import 'froala-editor/js/languages/uk';
import 'froala-editor/js/languages/tr';
import 'froala-editor/js/languages/el';
import 'froala-editor/js/languages/pt_pt';
import 'froala-editor/js/languages/pl';
import 'froala-editor/js/languages/sk';

import { Router } from '@angular/router';
import { LoggedIn } from '@state/actions/auth/auth.actions';
import { forkJoin } from 'rxjs';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '@environments/environment';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { VersionService } from '@shared/services/version.service';

export function createTranslateLoader(http: HttpClient) {
    const version = environment.appVersion !== '__APP_VERSION__' ? `?v=${environment.appVersion}` : '';
    return new TranslateHttpLoader(http, './assets/i18n/', '.json' + version);
}

export function appInit(appInitService: AppInitService) {
    return () => appInitService.onInit();
}

export function checkVersionFactory(versionService: VersionService) {
    return () => versionService.checkVersion();
}

@NgModule({
    declarations: [
        AppComponent
    ],
    imports: [
        HttpClientModule,
        BrowserAnimationsModule, // required for ng2-tag-input
        CoreModule,
        LayoutModule,
        SharedModule.forRoot(),
        RoutesModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient]
            }
        }),
        StateModule,
        StoreModule.forRoot(reducers, { metaReducers }),
        EffectsModule.forRoot([]),
        NgxLoadingModule.forRoot({
            animationType: ngxLoadingAnimationTypes.threeBounce,
            backdropBackgroundColour: 'rgba(0, 0, 0, 0.05)',
            fullScreenBackdrop: true,
            primaryColour: '#2581C4',
            secondaryColour: '#6E9DD4',
            tertiaryColour: '#96ABC4'
        }),
        FontAwesomeModule,
        IdentityServerConfigModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000'
        }),
        ToastModule
    ],
    providers: [
        AppInitService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpLoggingInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpAuthInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appInit,
            multi: true,
            deps: [AppInitService, AuthService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: checkVersionFactory,
            deps: [VersionService],
            multi: true
        },
        MessageService
    ]
})
export class AppModule implements DoBootstrap {

    private isSupportProfile: boolean;

    constructor(logging: LoggingService, private readonly eventService: PublicEventsService,
        private router: Router, private oidcSecurityService: OidcSecurityService, private store: Store<IState>) {
        logging.log.info('AppModule', 'done');

        this.store.select(x => x.app.profile.isSupport)
            .subscribe(isSupport => {
                this.isSupportProfile = isSupport;
            });

        // existing browser-window (=page-reloads) MUST have precedence over new windows
        const mruTenant = sessionStorage.getItem('mruTenant') ?? '';
        if (mruTenant === '') {
            // we assume that an empty session-storage means we have started a new browser window

            // in order for mruTenant to be retained between browser-windows
            // we copy the localstorage into session storage
            sessionStorage.setItem('mruTenant', localStorage.getItem('mruTenant'));
            sessionStorage.setItem('mruLoginHint', localStorage.getItem('mruLoginHint'));
        }
        const oidc$ = this.eventService.registerForEvents();
        oidc$.subscribe((e) => {
            logging.log.info('OIDC event received', `type: ${e.type}`);
            if (e.type === EventTypes.TokenExpired) {
                logging.log.info('OIDC token expired.');
            }
            else if (e.type === EventTypes.ConfigLoadingFailed) {
                logging.log.info('OIDC configuration could not be loaded. Verify identity-server is up and running.');
            }
            else if (e.type === EventTypes.NewAuthenticationResult) {
                logging.log.info('OIDC NewAuthenticationResult');
                if (e.value?.isRenewProcess) {
                    if (e.value?.validationResult === 'LoginRequired') {
                        logging.log.info('OIDC NewAuthenticationResult: Renew-LoginRequired');
                        if (this.isSupportProfile) {
                            // support profiles do not support (re-)login
                            this.router.navigate(['/support-expired']);
                        }
                        else {
                            // we (re)-navigate to a guarded route.
                            // because the access token is expired,
                            // the guard will route us to the identity-server login
                            window.location.reload();
                        }
                    }
                    if (e.value?.isAuthenticated) {
                        logging.log.info('OIDC NewAuthenticationResult: Renew-Success');

                        const configs = this.oidcSecurityService.getConfigurations();
                        const getAccessTokens$ = configs.map(cf => this.oidcSecurityService.getAccessToken(cf.configId));
                        forkJoin(getAccessTokens$).subscribe(responses => {
                            // we assume that token that is most recently issued
                            // is the renewed token
                            const renewedToken = this.findMostRecentlyIssuedAt(responses);
                            this.store.dispatch(new LoggedIn({ token: renewedToken }));
                        });
                    }
                }
            }
        });
    }

    private findMostRecentlyIssuedAt(tokens: string[]): string {

        let maxIssuedAt = 0;
        let maxIatToken = null;
        tokens.forEach(tokenString => {
            const token = new Token(tokenString);
            if (token.decoded && token.decoded.iat > maxIssuedAt) {
                maxIssuedAt = token.decoded.iat;
                maxIatToken = tokenString;
            }
        });

        return maxIatToken;
    }

    public ngDoBootstrap(ref: ApplicationRef) {
        ref.bootstrap(AppComponent);
    }
}
